import moment from 'moment-timezone';
import deepEqual from 'fast-deep-equal';
import { Playlist, Frequency } from '@raydiant/api-client-js';

export type RepeatOption = 'none' | 'daily' | 'annually' | 'weekday' | 'custom';
export type CustomFrequency = 'week' | 'day' | 'month' | 'year';
export type Weekday = 'SU' | 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA';

export const WEEKDAYS: { key: Weekday; label: string; ariaLabel: string }[] = [
  { key: 'SU', label: 'Sun', ariaLabel: 'Sunday' },
  { key: 'MO', label: 'Mon', ariaLabel: 'Monday' },
  { key: 'TU', label: 'Tue', ariaLabel: 'Tuesday' },
  { key: 'WE', label: 'Wed', ariaLabel: 'Wednesday' },
  { key: 'TH', label: 'Thu', ariaLabel: 'Thursday' },
  { key: 'FR', label: 'Fri', ariaLabel: 'Friday' },
  { key: 'SA', label: 'Sat', ariaLabel: 'Saturday' },
];
export const WEEKDAY_BY_INDEX: Weekday[] = [
  'SU',
  'MO',
  'TU',
  'WE',
  'TH',
  'FR',
  'SA',
];
export const ALL_WEEKDAYS: Weekday[] = [
  'MO',
  'TU',
  'WE',
  'TH',
  'FR',
  'SA',
  'SU',
];
const WORK_WEEKDAYS: Weekday[] = ['MO', 'TU', 'WE', 'TH', 'FR'];

export const getCurrentWeekday = (): Weekday => {
  return WEEKDAY_BY_INDEX[moment().day()] || 'MO';
};

export const CUSTOM_FREQ_OPTIONS: { key: CustomFrequency; label: string }[] = [
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
];

export const customFreqToFrequency = (freq: CustomFrequency): Frequency => {
  switch (freq) {
    case 'week':
      return Frequency.weekly;
    case 'month':
      return Frequency.monthly;
    case 'day':
      return Frequency.weekly;
    case 'year':
      return Frequency.monthly;
    default:
      return Frequency.weekly;
  }
};

export const getRepeatOptions = (
  startDate: string,
): { key: RepeatOption; label: string }[] => {
  const annuallyLabel = startDate
    ? `Annually on ${moment(startDate).format('MMMM Do')}`
    : 'Annually';

  return [
    { key: 'none', label: 'Does not repeat' },
    { key: 'daily', label: 'Daily' },
    { key: 'annually', label: annuallyLabel },
    { key: 'weekday', label: 'Every weekday (Monday to Friday)' },
    { key: 'custom', label: 'Custom' },
  ];
};

export const repeatOptionToRecurrenceRule = (
  option: RepeatOption,
): Playlist['recurrenceRule'] => {
  switch (option) {
    case 'weekday':
      return {
        dtstart: '',
        freq: Frequency.weekly,
        interval: 1,
        byday: [...WORK_WEEKDAYS],
      };
    case 'daily':
      return {
        dtstart: '',
        freq: Frequency.weekly,
        interval: 1,
        byday: [...ALL_WEEKDAYS],
      };
    case 'annually':
      return { dtstart: '', freq: Frequency.monthly, interval: 12 };
    case 'custom':
      return { dtstart: '', freq: Frequency.weekly, interval: 1 };
    default:
      return null;
  }
};

export const recurrenceRuleToRepeatOption = (
  rule: Playlist['recurrenceRule'],
): RepeatOption => {
  if (!rule) return 'none';

  const bymonth = (rule as unknown as { bymonth?: number[] }).bymonth;
  const interval = rule.interval ?? 1;

  if (
    rule.freq === Frequency.monthly &&
    interval === 12 &&
    (!bymonth || bymonth.length === 0)
  )
    return 'annually';

  if (rule.freq === Frequency.weekly && interval === 1 && rule.byday) {
    const sorted = [...rule.byday].sort();
    if (deepEqual(sorted, [...ALL_WEEKDAYS].sort())) return 'daily';
    if (deepEqual(sorted, [...WORK_WEEKDAYS].sort())) return 'weekday';
  }

  return 'custom';
};

export const datetimeFormat = 'YYYY-MM-DDTHH:mm:ss';
export const PLAY_FOREVER_DATE = '2100-01-01';

export const toDateInputValue = (value: string | null) => {
  return value ? moment(value).format('YYYY-MM-DD') : '';
};

export const toTimeInputValue = (value: string | null) => {
  return value ? moment(value).format('HH:mm') : '';
};

export const combineDatetime = (date: string, time: string) => {
  const datetime = moment(date);
  const [hour, minute] = time.split(':');
  datetime.hour(parseInt(hour, 10));
  datetime.minute(parseInt(minute, 10));
  datetime.second(0);
  datetime.millisecond(0);
  return datetime.format(datetimeFormat);
};

export const isPlayForeverDatetime = (value: string | null) => {
  if (!value) return false;
  const parsed = moment.parseZone(value, moment.ISO_8601, true);
  if (!parsed.isValid()) return false;
  // Product behavior: any datetime on the sentinel day means "repeats forever".
  return parsed.format('YYYY-MM-DD') === PLAY_FOREVER_DATE;
};

export const toPlayForeverDateInputValue = () => {
  return PLAY_FOREVER_DATE;
};

export const toMonthDayValue = (value: string) => {
  const parsed = moment(value, 'YYYY-MM-DD', true);
  if (!parsed.isValid()) return 1;
  return Math.min(31, Math.max(1, parsed.date()));
};

export const toYearRepeatDateValue = (value: string) => {
  const parsed = moment(value, 'YYYY-MM-DD', true);
  if (!parsed.isValid()) return moment().format('YYYY-MM-DD');
  return parsed.format('YYYY-MM-DD');
};

export const parseYearRepeatParts = (value: string) => {
  const parsed = moment(value, 'YYYY-MM-DD', true);
  if (!parsed.isValid()) return null;
  return {
    bymonth: parsed.month() + 1,
    bymonthday: parsed.date(),
  };
};

export const toYearDateFromRule = (
  rule: Playlist['recurrenceRule'],
  fallbackDate: string,
) => {
  const fallback = toYearRepeatDateValue(fallbackDate);
  if (!rule) return fallback;

  const bymonth = (rule as unknown as { bymonth?: number[] }).bymonth;
  const bymonthday = (rule as unknown as { bymonthday?: number[] }).bymonthday;
  const month = bymonth?.[0];
  const day = bymonthday?.[0];
  if (!month || !day) return fallback;

  const year = moment().year();
  const candidate = moment(
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    'YYYY-MM-DD',
    true,
  );
  if (candidate.isValid()) return candidate.format('YYYY-MM-DD');

  const endOfMonth = moment(
    `${year}-${String(month).padStart(2, '0')}-01`,
    'YYYY-MM-DD',
    true,
  ).endOf('month');
  return endOfMonth.format('YYYY-MM-DD');
};

export const toLegacyRecurrenceRule = ({
  rule,
  repeatOption,
  customFreq,
  customInterval,
  startDate,
  customYearDate,
}: {
  rule: Playlist['recurrenceRule'];
  repeatOption: RepeatOption;
  customFreq: CustomFrequency;
  customInterval: number;
  startDate: string;
  customYearDate: string;
}): Playlist['recurrenceRule'] => {
  if (!rule) return null;

  if (repeatOption === 'daily') {
    return {
      ...rule,
      freq: Frequency.weekly,
      interval: 1,
      byday: ALL_WEEKDAYS,
    };
  }

  if (repeatOption === 'annually') {
    const day = toMonthDayValue(startDate);
    return {
      ...rule,
      freq: Frequency.monthly,
      interval: 12,
      bymonthday: [day],
    } as unknown as Playlist['recurrenceRule'];
  }

  if (repeatOption === 'custom' && customFreq === 'year') {
    const yearParts = parseYearRepeatParts(customYearDate);
    return {
      ...rule,
      freq: Frequency.monthly,
      interval: Math.max(customInterval, 1) * 12,
      ...(yearParts
        ? ({
            bymonth: [yearParts.bymonth],
            bymonthday: [yearParts.bymonthday],
          } as unknown as Record<string, unknown>)
        : {}),
    } as unknown as Playlist['recurrenceRule'];
  }

  return rule;
};

export const openNativePicker = (input: HTMLInputElement | null) => {
  if (!input) return;
  const pickerInput = input as HTMLInputElement & {
    showPicker?: () => void;
  };

  if (typeof pickerInput.showPicker === 'function') {
    pickerInput.showPicker();
    return;
  }

  input.focus();
  input.click();
};

export const localTimezone = moment.tz.guess();
